const MAX_DAYS = 3650;
const MAX_MONTHS = 600;

/**
 * Parse free-text duration ("90 Days", "2 Months", "1Day", "1.2Y") → month count.
 * Used for projected-return math (fractional-friendly via durationToMonthsForCalc).
 */
export function parseDurationToMonths(str) {
  if (!str) return null;
  const s = String(str).toLowerCase().trim();
  const numMatch = s.match(/(-?\d+(?:\.\d+)?)/);
  if (!numMatch) return null;
  const n = Number(numMatch[1]);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (/year|\byr\b|\d\s*y\b/.test(s)) return Math.max(1, Math.round(n * 12));
  if (/day/.test(s)) return Math.max(1 / 30, n / 30);
  if (/week|\bwk/.test(s)) return Math.max(1 / 30, (n * 7) / 30);
  if (/month|\bmo\b/.test(s)) return Math.max(1, Math.round(n));
  return Math.max(1, Math.round(n));
}

export function parseMonthsText(monthsStr, durationStr) {
  const fromMonths = parseDurationToMonths(monthsStr);
  if (fromMonths != null) return fromMonths;
  const fromDuration = parseDurationToMonths(durationStr);
  if (fromDuration != null) return fromDuration;
  return 12;
}

/** Parse legacy text into explicit day/month parts for storage. */
export function parseLegacyDurationParts(monthsStr, durationStr) {
  const primary = String(monthsStr || '').trim();
  const fallback = String(durationStr || '').trim();
  const source = primary || fallback;
  if (!source) return { days: 0, months: 1 };

  const s = source.toLowerCase();
  const numMatch = s.match(/(-?\d+(?:\.\d+)?)/);
  if (!numMatch) return { days: 0, months: 1 };
  const n = Math.round(Number(numMatch[1]));
  if (!Number.isFinite(n) || n <= 0) return { days: 0, months: 1 };

  if (/day/.test(s)) return { days: Math.min(n, MAX_DAYS), months: 0 };
  if (/week|\bwk/.test(s)) return { days: Math.min(n * 7, MAX_DAYS), months: 0 };
  if (/month|\bmo\b/.test(s)) return { days: 0, months: Math.min(n, MAX_MONTHS) };
  if (/year|\byr\b|\d\s*y\b/.test(s)) {
    return { days: 0, months: Math.min(Math.round(n * 12), MAX_MONTHS) };
  }

  if (primary && /day/.test(String(monthsStr).toLowerCase())) {
    return { days: Math.min(n, MAX_DAYS), months: 0 };
  }
  if (fallback && /day/.test(fallback.toLowerCase()) && !primary) {
    return { days: Math.min(n, MAX_DAYS), months: 0 };
  }

  return { days: 0, months: Math.min(n, MAX_MONTHS) };
}

export function normalizePlanDuration(daysRaw, monthsRaw) {
  let days = Math.floor(Number(daysRaw));
  let months = Math.floor(Number(monthsRaw));
  if (!Number.isFinite(days) || days < 0) days = 0;
  if (!Number.isFinite(months) || months < 0) months = 0;
  days = Math.min(days, MAX_DAYS);
  months = Math.min(months, MAX_MONTHS);
  if (days === 0 && months === 0) months = 1;
  return { days, months };
}

export function resolvePlanDuration(plan) {
  if (!plan || typeof plan !== 'object') return { days: 0, months: 1 };

  const hasNumeric =
    plan.durationDays != null || plan.durationMonthsCount != null;

  if (hasNumeric) {
    return normalizePlanDuration(plan.durationDays, plan.durationMonthsCount);
  }

  return parseLegacyDurationParts(plan.months, plan.duration);
}

export function durationToMonthsForCalc({ days, months }) {
  const d = Math.max(0, Number(days) || 0);
  const m = Math.max(0, Number(months) || 0);
  const total = m + d / 30;
  return total > 0 ? total : 1 / 30;
}

export function addDurationToDate(startDate, { days, months }) {
  const start = new Date(startDate);
  if (Number.isNaN(start.getTime())) return null;
  const end = new Date(start);
  const m = Math.max(0, Number(months) || 0);
  const d = Math.max(0, Number(days) || 0);
  if (m > 0) end.setMonth(end.getMonth() + m);
  if (d > 0) end.setDate(end.getDate() + d);
  return end;
}

/** English storage string for legacy `months` field on Plan. */
export function buildDurationText({ days, months }) {
  const { days: d, months: m } = normalizePlanDuration(days, months);
  if (m === 0 && d > 0) return d === 1 ? '1 Day' : `${d} Days`;
  if (d === 0 && m > 0) return m === 1 ? '1 Month' : `${m} Months`;
  if (d > 0 && m > 0) return `${m} Months ${d} Days`;
  return '1 Month';
}

export function serializePlanDurationForClient(plan) {
  if (!plan || typeof plan !== 'object') return plan;
  const term = resolvePlanDuration(plan);
  return {
    ...plan,
    durationDays: term.days,
    durationMonthsCount: term.months,
    months: buildDurationText(term),
  };
}

export async function backfillMissingPlanDurationFields(PlanModel) {
  const plans = await PlanModel.find({
    $or: [
      { durationDays: { $exists: false } },
      { durationMonthsCount: { $exists: false } },
    ],
  }).lean();

  for (const plan of plans) {
    const term = parseLegacyDurationParts(plan.months, plan.duration);
    await PlanModel.updateOne(
      { _id: plan._id },
      {
        $set: {
          durationDays: term.days,
          durationMonthsCount: term.months,
          months: buildDurationText(term),
        },
      }
    );
  }
}

export function formatPlanDurationLabel(t, plan) {
  const { days, months } = resolvePlanDuration(plan);
  return formatDurationPeriod(t, days, months);
}

export function formatDurationPeriod(t, days, months) {
  const { days: d, months: m } = normalizePlanDuration(days, months);
  if (m === 0 && d > 0) {
    if (d === 1) return t('user.investments.duration.oneDay');
    return t('user.investments.duration.daysOnly', { days: d });
  }
  if (d === 0 && m > 0) {
    if (m === 1) return t('user.investments.duration.oneMonth');
    return t('user.investments.duration.monthsOnly', { months: m });
  }
  if (d > 0 && m > 0) {
    return t('user.investments.duration.daysAndMonths', { days: d, months: m });
  }
  return t('user.investments.duration.oneMonth');
}

export function formatInvestmentDurationLabel(t, investment) {
  const days = Number(investment?.durationDays) || 0;
  const months = Number(investment?.durationMonths) || 0;
  if (days > 0 || months > 0) {
    return formatDurationPeriod(t, days, months);
  }
  return '—';
}
